import type { MemberDto } from "@ied/shared-types";
import { ArrowDown, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Importer } from "./importer";
import { useMemberTableContext } from "./members-table-provider";

export function TableOptions() {
	const {
		groups,
		groupsFetched,
		members,
		selectedGroup,
		setSelectedGroup,
		setPageIndex,
	} = useMemberTableContext();

	return (
		<>
			{groupsFetched && groups && (
				<div className="flex justify-end items-end space-x-2">
					<div className="space-y-1">
						<Label htmlFor="group-filter">Filter by group</Label>
						<Select
							id="group-filter"
							value={selectedGroup}
							onValueChange={(value) => {
								if (value) {
									setSelectedGroup(value);
									setPageIndex(0); // Reset to first page on filter change
								}
							}}
						>
							<SelectTrigger className="w-[180px] m-0">
								<SelectValue />
							</SelectTrigger>
							<SelectContent alignItemWithTrigger={false}>
								<SelectItem value="all">All</SelectItem>
								{groups.map((g) => (
									<SelectItem key={g.id} value={g.group_name}>
										{g.group_name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger render={<Button variant="outline" />}>
							Send Email <ChevronDown />
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuGroup>
								<DropdownMenuLabel>Via:</DropdownMenuLabel>
								{(["gmail", "native", "copy"] as const).map((option) => (
									<DropdownMenuItem
										key={option}
										onClick={() =>
											handleEmailListedUsers(option, members?.items)
										}
									>
										{option}
									</DropdownMenuItem>
								))}
								<DropdownMenuSeparator />
							</DropdownMenuGroup>
						</DropdownMenuContent>
					</DropdownMenu>
					<Importer />
				</div>
			)}
		</>
	);
}

function handleEmailListedUsers(
	method: "gmail" | "native" | "copy",
	members: MemberDto[] | undefined,
) {
	if (!members || members.length === 0) {
		toast("No members selected to email");
		return;
	}

	if (members.length > 50) {
		toast("Too many members selected, limit is 50");
		return;
	}

	let commaSeperatedEmails = members.reduce((acc, curr) => {
		acc += `${curr.email},`;
		return acc;
	}, "");
	commaSeperatedEmails = commaSeperatedEmails.slice(0, -1);

	if (method === "gmail") {
		const url = new URL("/mail", "https://mail.google.com");

		url.searchParams.append("view", "cm");
		url.searchParams.append("tf", "1");
		url.searchParams.append("to", commaSeperatedEmails);

		window.open(url.toString(), "_blank", "noopener,noreferrer");
	} else if (method === "native") {
		const url = `mailto:${commaSeperatedEmails}`;
		window.open(url.toString(), "_blank", "noopener,noreferrer");
	} else {
		navigator.clipboard.writeText(commaSeperatedEmails);
		toast("Emails copied to clipboard");
	}
}
